import { useEffect, useState } from 'react';

import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';

import { useKeyboardContext, useKeyboardShortcuts } from '@/core/keyboard';

import { usePosHardware } from '@/pages/pos/PosHardwareContext';

import type { ProductResponse } from '@/types/product';

import { usePosBilling } from '@/pages/pos/PosBillingContext';

import { PosModifierModal } from '@/components/pos/billing/PosModifierModal';

import { isFbModifiersEnabled } from '@/utils/posModifiers';

import { formatCurrency } from '@/utils/format';

import { resolveFileUrl } from '@/utils/fileUrl';

import { showApiError } from '@/utils/apiError';
import { Button } from '@/components/ui/Button';
import { usePrompt } from '@/components/ui/PromptDialog';
import { notify } from '@/core/toast/notify';



function productImageUrl(product: ProductResponse): string | undefined {

  const raw =

    product.imageUrl ??

    product.images?.find((img) => img.primary)?.imageUrl ??

    product.images?.[0]?.imageUrl;

  return resolveFileUrl(raw);

}



export function PosProductPane() {
  const { prompt, dialog: promptDialog } = usePrompt();
  const { setFocusZone, setStatusHint } = useKeyboardContext();

  const {

    searchRef,

    search,

    setSearch,

    products,

    productIndex,

    setProductIndex,

    addHighlightedProduct,

    addProduct,

    addProductWithModifiers,

    storeId,

    currencyCode,

  } = usePosBilling();

  const { config, barcodePrinterReady, printBarcodeLabel } = usePosHardware();

  const fmt = (n: number | string) => formatCurrency(n, currencyCode);

  const [scaleWeight, setScaleWeight] = useState('');

  const [modifierProduct, setModifierProduct] = useState<ProductResponse | null>(null);

  const fbMode = isFbModifiersEnabled(storeId);



  const scaleEnabled = config?.scaleEnabled ?? false;

  const labelPrintEnabled = barcodePrinterReady;



  useEffect(() => {

    if (!scaleEnabled) return;

    const onSerial = (e: Event) => {

      const detail = (e as CustomEvent<{ weight?: number }>).detail;

      if (detail?.weight != null && detail.weight > 0) {

        setScaleWeight(String(detail.weight));

      }

    };

    window.addEventListener('pos-scale-weight', onSerial);

    return () => window.removeEventListener('pos-scale-weight', onSerial);

  }, [scaleEnabled]);



  const printHighlightedLabel = async (copies = 1) => {

    const p = products[productIndex];

    if (!p) {

      notify.error('Select a product first');

      return;

    }

    try {

      await printBarcodeLabel(p.id, copies);

    } catch (err) {

      showApiError(err, 'Label print failed');

    }

  };



  useKeyboardShortcuts(
    [
      {
        id: 'pos-f11-label',
        keys: ['f11'],
        label: 'F11',
        description: 'Print barcode label',
        category: 'Product',
        enabled: () => labelPrintEnabled && products.length > 0,
        handler: () => {
          void printHighlightedLabel(1);
        },
      },
    ],
    [labelPrintEnabled, products.length, productIndex, printHighlightedLabel],
  );

  const addWithScale = () => {

    const p = products[productIndex];

    if (!p) return;

    const w = Number(scaleWeight);

    addProduct(p, w > 0 ? w : 1);

    setScaleWeight('');

  };



  const pickProduct = (p: ProductResponse) => {

    if (scaleEnabled && scaleWeight) {

      const w = Number(scaleWeight);

      addProduct(p, w > 0 ? w : 1);

      setScaleWeight('');

      return;

    }

    if (fbMode) {

      setModifierProduct(p);

      return;

    }

    addProduct(p);

  };



  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {

    if (e.key === 'ArrowDown') {

      e.preventDefault();

      setProductIndex((i) => Math.min(i + 1, Math.max(0, products.length - 1)));

    } else if (e.key === 'ArrowUp') {

      e.preventDefault();

      setProductIndex((i) => Math.max(i - 1, 0));

    } else if (e.key === 'Enter') {

      e.preventDefault();

      if (products.length) {

        if (scaleEnabled && scaleWeight) addWithScale();

        else addHighlightedProduct();

      }

    }

  };



  return (
    <>
    {promptDialog}
    <section className="flex w-[min(100%,20rem)] shrink-0 flex-col border-r border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950 sm:w-80">

      {modifierProduct && (

        <PosModifierModal

          open

          storeId={storeId}

          productName={modifierProduct.name}

          basePrice={Number(modifierProduct.sellingPrice)}

          onClose={() => setModifierProduct(null)}

          onConfirm={(modifiers, extra) => {

            addProductWithModifiers(modifierProduct, modifiers, extra);

            setModifierProduct(null);

          }}

        />

      )}

      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">

        <label className="mb-1 flex items-center text-xs font-medium text-slate-600 dark:text-slate-400">
          Barcode / product search
        </label>

        <input

          ref={searchRef}

          id="pos-product-search"

          type="text"

          value={search}

          onChange={(e) => setSearch(e.target.value)}

          onKeyDown={onSearchKeyDown}

          onFocus={() => {

            setFocusZone('product');

            setStatusHint(
              labelPrintEnabled
                ? '↑↓ navigate · Enter add · Label print available'
                : '↑↓ navigate · Enter add · scan barcode',
            );

          }}

          placeholder="Scan barcode or type SKU / name"

          className="w-full rounded-lg border-2 border-slate-300 bg-white px-4 py-3 text-lg font-medium text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-slate-600 dark:bg-slate-800 dark:text-white"

          autoComplete="off"

        />

        {scaleEnabled && (

          <div className="mt-2 flex gap-2">

            <input

              type="number"

              min={0.001}

              step="0.001"

              value={scaleWeight}

              onChange={(e) => setScaleWeight(e.target.value)}

              placeholder={`Weight (${config?.scaleUnit ?? 'kg'})`}

              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"

            />

            <Button type="button" onClick={addWithScale} disabled={!products.length}>

              Weigh & add

            </Button>

          </div>

        )}

        {labelPrintEnabled && products.length > 0 && (

          <div className="mt-2 flex items-center gap-2">

            <Button

              type="button"

              variant="secondary"

              size="sm"

              onClick={() => void printHighlightedLabel(1)}

            >

              <LocalPrintshopOutlinedIcon sx={{ fontSize: 16 }} />

              Print label
            </Button>

            <button

              type="button"

              className="text-xs text-slate-500 hover:text-primary-600"

              onClick={() => {
                void prompt({
                  title: 'Print labels',
                  label: 'Number of labels',
                  defaultValue: '1',
                }).then((raw) => {
                  if (!raw) return;
                  const n = Number(raw);
                  if (n > 0 && n <= 50) void printHighlightedLabel(n);
                });
              }}

            >

              Multiple copies…

            </button>

          </div>

        )}

      </div>

      <div className="flex-1 overflow-y-auto p-2" role="listbox" aria-label="Product suggestions">

        {search.trim() && products.length === 0 && (

          <div className="flex min-h-[120px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 p-6 text-center dark:border-slate-600">

            <p className="text-sm text-slate-500">No products — try another code or name</p>

          </div>

        )}

        {!search.trim() && (

          <div className="flex min-h-[120px] flex-col items-center justify-center p-6 text-center text-slate-500">

            <Inventory2OutlinedIcon className="mb-2 !text-4xl opacity-40" />

            <p className="text-sm">Scan or search to add items</p>

          </div>

        )}

        {products.map((p, i) => {

          const selected = i === productIndex;

          const img = productImageUrl(p);

          return (

            <button

              key={p.id}

              type="button"

              role="option"

              aria-selected={selected}

              onClick={() => pickProduct(p)}

              onMouseEnter={() => setProductIndex(i)}

              className={`mb-1 flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition ${

                selected

                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20 dark:bg-primary-950/40'

                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800'

              }`}

            >

              {img ? (

                <img src={img} alt="" className="h-11 w-11 shrink-0 rounded object-cover" />

              ) : (

                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-slate-700">

                  <Inventory2OutlinedIcon className="text-slate-400" />

                </div>

              )}

              <div className="min-w-0 flex-1">

                <p className="truncate font-semibold text-slate-900 dark:text-white">{p.name}</p>

                <p className="truncate text-xs text-slate-500">{p.sku}</p>

              </div>

              <p className="shrink-0 text-base font-bold text-primary-600 dark:text-primary-400">

                {fmt(p.sellingPrice)}

              </p>

              {labelPrintEnabled && (

                <span

                  role="button"

                  tabIndex={-1}

                  className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-primary-600 dark:hover:bg-slate-700"

                  title="Print barcode label"

                  onClick={(e) => {

                    e.stopPropagation();

                    void printBarcodeLabel(p.id, 1);

                  }}

                >

                  <LocalPrintshopOutlinedIcon sx={{ fontSize: 20 }} />

                </span>

              )}

            </button>

          );

        })}

      </div>

    </section>
    </>
  );

}


